import type { AssistantMessagesState } from './assistantMessagesOperations';
import { createChatMessage } from './chatMessageFactory';
import { isPlanMessage } from './types';
import type {
  AgentPlanEvent,
  PlanContent,
  PlanMessage,
  PlanProposedEvent,
  PlanTask,
} from './types';

const toProposedTask = (task: PlanTask): PlanContent['tasks'][number] => ({
  ...task,
  status: 'pending',
});

const toReplannedTask = (
  task: PlanTask,
  content: PlanContent
): PlanContent['tasks'][number] => {
  const previous = content.tasks.find(({ id }) => id === task.id);
  if (previous?.status === 'done') return { ...task, status: 'done' };
  return toProposedTask(task);
};

const toProposedContent = (event: PlanProposedEvent): PlanContent => ({
  goal: event.plan.goal,
  tasks: event.plan.tasks.map(toProposedTask),
  status: 'proposed',
});

const replacePlan = (
  content: PlanContent,
  event: PlanProposedEvent
): PlanContent => {
  if (content.status !== 'replanning') return toProposedContent(event);
  return {
    goal: event.plan.goal,
    tasks: event.plan.tasks.map(task => toReplannedTask(task, content)),
    status: 'running',
  };
};

const updateTask = (
  content: PlanContent,
  event: Extract<AgentPlanEvent, { type: 'task_started' | 'task_finished' }>
): PlanContent => {
  if (!content.tasks.some(({ id }) => id === event.task_id)) return content;
  if (event.type === 'task_finished' && 'reason' in event.outcome)
    return content;
  const status = event.type === 'task_started' ? 'running' : 'done';
  return {
    ...content,
    status: 'running',
    tasks: content.tasks.map(task => {
      if (task.id !== event.task_id || task.status === status) return task;
      return { ...task, status };
    }),
  };
};

const updatePlanContent = (
  content: PlanContent,
  event: AgentPlanEvent
): PlanContent => {
  switch (event.type) {
    case 'plan_proposed':
      return replacePlan(content, event);
    case 'task_started':
    case 'task_finished':
      return updateTask(content, event);
    case 'execution_failed':
      return { ...content, status: 'replanning' };
    case 'orchestration_concluded':
      return { ...content, status: event.outcome };
  }
};

const updatePlanMessage = (
  state: AssistantMessagesState,
  plan: PlanMessage,
  event: AgentPlanEvent
): AssistantMessagesState => {
  const content = updatePlanContent(plan.content, event);
  if (content === plan.content) return state;
  return {
    ...state,
    messages: state.messages.map(message => {
      if (message.messageId !== plan.messageId) return message;
      return { ...plan, content };
    }),
  };
};

const insertPlanMessage = (
  state: AssistantMessagesState,
  event: PlanProposedEvent & { exchangeId: string }
): AssistantMessagesState => {
  const message = createChatMessage({
    role: 'plan',
    exchangeId: event.exchangeId,
    content: toProposedContent(event),
  });
  const assistantIndex = state.messages.findIndex(
    message => message.messageId === state._activeExchange?.assistantMessageId
  );
  const insertionIndex =
    assistantIndex === -1 ? state.messages.length : assistantIndex;
  return {
    ...state,
    messages: [
      ...state.messages.slice(0, insertionIndex),
      message,
      ...state.messages.slice(insertionIndex),
    ],
  };
};

export const handlePlanEvent = (
  state: AssistantMessagesState,
  event: AgentPlanEvent
): AssistantMessagesState => {
  const plan = state.messages.find(
    (message): message is PlanMessage =>
      isPlanMessage(message) && message.exchangeId === event.exchangeId
  );
  if (plan) return updatePlanMessage(state, plan, event);
  if (event.type === 'plan_proposed') return insertPlanMessage(state, event);
  return state;
};
